import {useMemo} from "react";
import {Form, InputGroup, Table} from "react-bootstrap";
import {Link} from "react-router";
import {OrdersApi} from "../../api/OrdersApi";
import {OrderStatus} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {formatDateTime, formatPrice} from "../../components/format";
import {usePersistentState} from "../../components/usePersistentState";
import {OrderStatusBadge, providerName} from "../account/OrderParts";

const STATUSES: OrderStatus[] = ['fulfilled', 'pending', 'refunded', 'failed', 'cancelled'];
type StatusFilter = 'all' | OrderStatus;

const isString = (value: unknown): value is string => typeof value === 'string';
const isStatusFilter = (value: unknown): value is StatusFilter => value === 'all' || (STATUSES as unknown[]).includes(value);

// Every order, newest first. By default the sales (paid ones). Refunds are made in Stripe's or PayPal's dashboard, by
// the order's payment; the API then revokes the license.
export function OrdersAdminPage() {
    const {data: orders, error, loading} = useAsync(() => OrdersApi.getAll(), []);
    const [filter, setFilter] = usePersistentState('admin-orders:filter', '', isString);
    const [statusFilter, setStatusFilter] = usePersistentState<StatusFilter>('admin-orders:status', 'fulfilled', isStatusFilter);

    const shown = useMemo(() => {
        const text = filter.trim().toLowerCase();
        return (orders ?? [])
            .filter(x => statusFilter === 'all' || x.status === statusFilter)
            .filter(x => !text || [x.id, x.ownerUsername, x.licensee, x.productName].some(field => field.toLowerCase().includes(text)));
    }, [orders, filter, statusFilter]);
    const revenue = useMemo(() => {
        const paid = (orders ?? []).filter(x => x.status === 'fulfilled');
        const currency = paid[0]?.currency;
        return currency && paid.every(x => x.currency === currency)
            ? { currency, count: paid.length, total: paid.reduce((sum, x) => sum + x.subtotal, 0) }
            : undefined;
    }, [orders]);

    return (
        <>
            <PageHeader title="Orders"/>
            {loading && !orders && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            {orders && (
                <>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <InputGroup style={{ maxWidth: '24rem' }}>
                            <InputGroup.Text><i className="bi bi-search" aria-hidden="true"></i></InputGroup.Text>
                            <Form.Control placeholder="Order, customer or product" aria-label="Filter orders" value={filter} onChange={e => setFilter(e.target.value)}/>
                        </InputGroup>
                        <Form.Select className="w-auto" aria-label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as StatusFilter)}>
                            <option value="fulfilled">Paid</option>
                            <option value="pending">Processing</option>
                            <option value="refunded">Refunded</option>
                            <option value="failed">Declined</option>
                            <option value="cancelled">Expired</option>
                            <option value="all">All</option>
                        </Form.Select>
                        {revenue && (
                            <span className="ms-auto align-self-center small text-body-secondary">
                                {revenue.count} paid, {formatPrice(revenue.total, revenue.currency)} before tax
                            </span>
                        )}
                    </div>
                    {shown.length === 0 ? (
                        <p className="text-body-secondary">No orders match.</p>
                    ) : (
                        <Table responsive hover>
                            <thead>
                            <tr><th>Date</th><th>Order</th><th>Customer</th><th>Product</th><th className="text-end">Price</th><th className="text-end">Tax</th><th className="text-end">Total</th><th>Paid with</th><th>Status</th><th>License</th></tr>
                            </thead>
                            <tbody>
                            {shown.map(order => (
                                <tr key={order.id}>
                                    <td className="text-nowrap">{formatDateTime(order.createdOn)}</td>
                                    <td className="small font-monospace">{order.id}</td>
                                    <td>{order.ownerUsername}{order.licensee !== order.ownerUsername && <div className="small text-body-secondary">{order.licensee}</div>}</td>
                                    <td>{order.productName}, {order.edition}</td>
                                    <td className="text-end">{formatPrice(order.subtotal, order.currency)}</td>
                                    <td className="text-end">{formatPrice(order.tax, order.currency)}</td>
                                    <td className="text-end">{formatPrice(order.total, order.currency)}</td>
                                    <td>{providerName(order.provider)}</td>
                                    <td><OrderStatusBadge status={order.status}/></td>
                                    <td>{order.licenseId && <Link to={`/admin/licenses/${encodeURIComponent(order.licenseId)}`}>{order.licenseId}</Link>}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}
                </>
            )}
        </>
    );
}
